import { CITY_LIST_URLS, UPLOAD_CITIES } from '../../../../shared/fields';
import { shell } from '../../app';
import { NetworkError } from '../../data/api';
import { importCityFile } from '../../data/signals';
import { btn } from '../../ui/components';
import { h } from '../../ui/dom';

type UploadCity = (typeof UPLOAD_CITIES)[number];

const NAMES: Record<UploadCity, string> = { costa_mesa: 'Costa Mesa', laguna_beach: 'Laguna Beach' };
const ACCEPT =
  '.pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Costa Mesa and Laguna Beach refuse scripts, so their monthly new-business list comes in by hand
 *  (D61, owner decision): download it on the city's page, pick it here. The server reads it like the
 *  other cities' lists and says what it found in a file it cannot read. */
export const cityUpload = (onImported: () => void, preselect?: UploadCity): HTMLDetailsElement => {
  const city = h(
    'select',
    { class: 'field__input', 'aria-label': 'City' },
    // no city picked by default: a list stored under the wrong city takes the wrong name and links
    h('option', { value: '' }, 'Pick the city'),
    ...UPLOAD_CITIES.map((c) => h('option', { value: c }, NAMES[c])),
  );
  const page = h('a', { href: '#', target: '_blank', rel: 'noopener', hidden: true }, '');
  if (preselect) {
    // opened from the sources sheet for one city (D74)
    city.value = preselect;
    page.hidden = false;
    page.href = CITY_LIST_URLS[preselect];
    page.textContent = `Open ${NAMES[preselect]}'s page`;
  }
  city.addEventListener('change', () => {
    const picked = city.value as UploadCity | '';
    page.hidden = !picked;
    if (!picked) return;
    page.href = CITY_LIST_URLS[picked];
    page.textContent = `Open ${NAMES[picked]}'s page`;
  });
  const file = h('input', { type: 'file', accept: ACCEPT, class: 'field__input', 'aria-label': 'List file' });
  const go = btn('Import', { variant: 'primary', size: 'sm', onClick: () => void send() });

  const send = async () => {
    if (!city.value) return shell.toast('Pick the city first.');
    const picked = file.files?.[0];
    if (!picked) return shell.toast('Pick the file you downloaded first.');
    const name = NAMES[city.value as UploadCity];
    go.disabled = true;
    go.textContent = 'Importing…';
    try {
      const r = await importCityFile(city.value as UploadCity, picked);
      shell.toast(
        r.inserted
          ? `${r.inserted} new business${r.inserted === 1 ? '' : 'es'} from ${name}'s list`
          : `Nothing new in ${name}'s list (${r.kept} storefront${r.kept === 1 ? '' : 's'}, all already in)`,
      );
      file.value = '';
      onImported();
    } catch (err) {
      shell.toast(
        err instanceof NetworkError
          ? 'Importing needs a connection.'
          : err instanceof Error
            ? err.message
            : 'Could not import the file.',
        { kind: 'error', duration: 8000 },
      );
    } finally {
      go.disabled = false;
      go.textContent = 'Import';
    }
  };

  return h(
    'details',
    { class: 'section sig-howto' },
    h('summary', { class: 'section__title' }, 'Import a city file'),
    h(
      'p',
      { class: 'card__summary' },
      "Costa Mesa and Laguna Beach don't let the app read their lists. Once a month, open the city's page, download its new-business list (PDF or spreadsheet), then pick it here.",
    ),
    h('div', { class: 'sig-upload' }, city, page, file, go),
  );
};
